import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { getRouteMap } from "../../../pages/business/defaultPageDialog";
import { isAFHCategoryName } from "../../../pages/business/defaultPageDialog";
import { resolveDefaultPageIdForBusiness } from "../../../pages/business/defaultPageDialog";
import { GET_BUSINESS_CATEGORIES } from "../../../graphql/queries";
import { useSelectedBusiness } from "../../../redux/store";
import { isConciergeUser } from "../../../util/conciergeAccess";
import { hasStandaloneBookingAccess } from "../../../../core/business/productAccess";
import { APP_IDS, getRuntimeAppProfile } from "../../../runtime/appProfile";

export function getRuntimeManageUrl(runtimeAppId, hasBusinessContext) {
  if (runtimeAppId !== APP_IDS.TIMELY) return null;
  return hasBusinessContext
    ? "/biz/bookingtypes?v=booking"
    : "/biz/business?v=booking";
}

export function useManageUrl(providerUser) {
  const selectedBusiness = useSelectedBusiness();
  const { data: categoriesData } = useQuery(GET_BUSINESS_CATEGORIES);

  return useMemo(() => {
    const runtimeAppId = getRuntimeAppProfile().id;
    const hasBusinessContext =
      Number(selectedBusiness?.businessBasic?.id || 0) > 0 ||
      Number(providerUser?.defaultBusinessId || 0) > 0;

    const runtimeManageUrl = getRuntimeManageUrl(runtimeAppId, hasBusinessContext);
    if (runtimeManageUrl) return runtimeManageUrl;
    if (isConciergeUser(providerUser) && !hasBusinessContext) {
      return "/care/agent";
    }
    if (hasBusinessContext) return "/biz/dashboard?v=care";
    if (providerUser?.userId) return "/biz/business?v=care";
    if (!providerUser || !providerUser.url) return "/biz";
    const selectedCategory = (categoriesData?.getBusinessCategories || []).find(
      (c) => Number(c?.id) === Number(selectedBusiness?.businessBasic?.businessCategoryId)
    );
    const isAFHBusiness = isAFHCategoryName(selectedCategory?.name);
    const canUseStandaloneBooking = hasStandaloneBookingAccess(
      providerUser,
      isAFHBusiness
    );

    const routeMap = getRouteMap({
      businessUrl: providerUser.url,
      staffUrl: providerUser.staffUrl, // <-- needed for wallet path
      isAFHBusiness,
      canUseStandaloneBooking,
    });

    const preferredPageId = resolveDefaultPageIdForBusiness(
      providerUser.defaultPageId || (isAFHBusiness ? 1 : 2),
      isAFHBusiness,
      canUseStandaloneBooking
    );
    const fallbackRouteId = isAFHBusiness ? 1 : 2;
    const fallback =
      routeMap[preferredPageId] || routeMap[fallbackRouteId] || "/biz";

    try {
      const raw = localStorage.getItem("lastVertical");
      if (!raw) return fallback;

      const key = String(raw).toLowerCase();

      // Case A: numeric page id stored
      const asNum = Number(key);
      if (!Number.isNaN(asNum) && routeMap[asNum]) {
        const normalizedPageId = resolveDefaultPageIdForBusiness(asNum, isAFHBusiness);
        return routeMap[normalizedPageId] || fallback;
      }

      // Case B: map vertical string -> page id
      // Keep this in one place so it's easy to evolve
      const verticalToPageId = {
        care: isAFHBusiness ? 1 : 2,
        booking: canUseStandaloneBooking ? 2 : (isAFHBusiness ? 1 : 2),
        wallet: isAFHBusiness ? 4 : 2,
        // rentals isn't in routeMap yet; send them to dashboard for now
        rentals: isAFHBusiness ? 1 : 2,
      };

      const pageId = verticalToPageId[key];
      if (pageId && routeMap[pageId]) {
        return routeMap[pageId];
      }

      // Case C: last-ditch path match (defensive)
      const candidate = Object.values(routeMap).find(
        (p) => typeof p === "string" && p.toLowerCase().includes(`/${key}`)
      );
      if (candidate) return candidate;

      return fallback;
    } catch {
      // localStorage might be unavailable; fall back gracefully
      return fallback;
    }
  }, [
    providerUser,
    categoriesData?.getBusinessCategories,
    selectedBusiness?.businessBasic?.id,
    selectedBusiness?.businessBasic?.businessCategoryId,
  ]);
} 
